import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { HiExclamationTriangle } from 'react-icons/hi2';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { SelectInput } from '@src/components';
import FileInput from '@src/components/FileInput';
import {
  FiscalRepCancellationProofType,
  FiscalRepSubscriptionRowFragment,
  useRequestFiscalRepCancellationMutation,
} from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import { verifiableDocumentSchema } from '../../utils/verifiableDocument';

interface RequestCancellationFormProps {
  subscription: FiscalRepSubscriptionRowFragment;
}

const PROOF_OF_CANCELLATION_VALUES = [
  'CERTIFICATE_OF_RESIDENCE',
  'NHR_REGISTRATION_REQUEST',
  'UPDATED_NIF_DOCUMENT',
  'PORTAL_SCREENSHOT',
] as const;

const cancellationFormSchema = z.object({
  documentType: z.enum(PROOF_OF_CANCELLATION_VALUES, {
    errorMap: () => ({ message: 'validation.required' }),
  }),
  proofOfCancellationDocument: verifiableDocumentSchema,
});

type CancellationFormData = z.infer<typeof cancellationFormSchema>;

export function RequestCancellationForm({
  subscription,
}: RequestCancellationFormProps): JSX.Element {
  const { t } = useTranslation('fiscal_rep');
  const existingCancellationRequest = subscription?.cancellationRequests.find(
    (c) => !c.isComplete,
  );
  const [requestCancellation] = useRequestFiscalRepCancellationMutation();
  const toast = useToast();
  const navigate = useNavigate();

  const isElectronicNotification =
    existingCancellationRequest?.type === 'ELECTRONIC_NOTIFICATIONS';

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<CancellationFormData>({
    defaultValues: {
      documentType:
        existingCancellationRequest?.cancellationDocumentType ?? undefined,
      proofOfCancellationDocument: {
        id: existingCancellationRequest?.proofOfCancellationDocument?.id,
        type: 'FISCAL_REP_CANCELLATION_REQUEST',
      },
    },
    resolver: zodResolver(cancellationFormSchema),
  });

  async function onSubmit(data: CancellationFormData): Promise<void> {
    return requestCancellation({
      variables: {
        input: {
          cancellationType: isElectronicNotification
            ? 'ELECTRONIC_NOTIFICATIONS'
            : 'PROOF_OF_FISCAL_REP_REMOVAL',
          subscriptionId: subscription.id,
          documentType: data.documentType,
          proofOfCancellationDocument: data.proofOfCancellationDocument,
        },
      },
    })
      .then(() => {
        toast.success(
          t(
            'request_cancellation.upload.success',
            'Your request for cancellation has been submitted.',
          ),
        );
        void navigate('/');
      })
      .catch((err) => {
        toast.error(logAndFormatError(err));
      });
  }

  const cancellationDocumentTypeOptions: {
    label: string;
    value: FiscalRepCancellationProofType;
  }[] = [
    {
      label: t(
        'request_cancellation.document_types.updated_nif_document',
        'Updated NIF Document',
      ),
      value: 'UPDATED_NIF_DOCUMENT',
    },
    {
      label: t(
        'request_cancellation.document_types.cerificate_of_residence',
        'Certificate of Residence',
      ),
      value: 'CERTIFICATE_OF_RESIDENCE',
    },
    {
      label: t(
        'request_cancellation.document_types.nhr_registration_request',
        'NHR Registration Request',
      ),
      value: 'NHR_REGISTRATION_REQUEST',
    },
  ];

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      <h2>
        {t(
          'request_cancellation.upload.heading',
          'Upload Proof of Cancellation',
        )}
      </h2>
      {isElectronicNotification ? (
        <>
          <p>
            {t(
              'request_cancellation.upload.eletronic_notifications_description',
              'In order to proceed, you will need to upload proof that your request has been made to enable electronic notifications on Portal das Finanças.',
            )}
          </p>
          <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <HiExclamationTriangle
                  className="h-5 w-5 text-yellow-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <Trans
                    t={t}
                    i18nKey="request_cancellation.upload.need_help_electronic_notifications"
                  >
                    Not sure how to enable electronic notifications?{' '}
                    <strong>
                      <a
                        className="text-yellow-700 underline"
                        href="https://bordr.com/guides/how-to-activate-electronic-notifications-on-portal-das-financas-and-remove-your-fiscal-representative"
                      >
                        See our guide.
                      </a>
                    </strong>
                  </Trans>
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <p>
            {t(
              'request_cancellation.upload.description',
              "Upload one of the following documents to show that you have removed Bordr's fiscal representative:",
            )}
          </p>
          <ul className="list-inside list-disc">
            <li>
              {t(
                'request_cancellation.document_types_long.updated_nif_document',
                'Updated NIF Document',
              )}
            </li>
            <li>
              {t(
                'request_cancellation.document_types_long.cerificate_of_residence',
                'Certificate of Residence (Certidão)',
              )}
            </li>
            <li>
              {t(
                'request_cancellation.document_types_long.nhr_registration_request',
                'NHR Registration Request (Residente Não Habitual Pedido de Inscrição)',
              )}
            </li>
          </ul>
          <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <HiExclamationTriangle
                  className="h-5 w-5 text-yellow-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <Trans t={t} i18nKey="request_cancellation.upload.need_help">
                    Need help getting your document?{' '}
                    <strong>
                      <a
                        className="text-yellow-700 underline"
                        href="https://support.bordr.com/article/361-how-to-cancel-your-fiscal-representation-with-bordr"
                      >
                        See our guide.
                      </a>
                    </strong>
                  </Trans>
                </p>
              </div>
            </div>
          </div>
        </>
      )}
      <div className="grid sm:grid-cols-2">
        <div className="space-y-4">
          {!isElectronicNotification && (
            <SelectInput.LabelledController
              name="documentType"
              label={t(
                'request_cancellation.upload.document_type_label',
                'Document Type',
              )}
              control={control}
              options={cancellationDocumentTypeOptions}
              placeholder={t(
                'translation:placeholders.select_one',
                'Select one',
              )}
            />
          )}
          <FileInput.LabelledController
            name="proofOfCancellationDocument.fileOne"
            label={
              isElectronicNotification
                ? t(
                    'request_cancellation.upload.electronic_notifications_document_label',
                    'Screenshot indicating that your request has been submitted',
                  )
                : t(
                    'request_cancellation.upload.document_label',
                    'Document Upload',
                  )
            }
            control={control}
            category="verifiableDocumentOne"
          />
        </div>
        <div />
      </div>
      <OrderNavigationButtons
        className="pt-16"
        previousUrl="/"
        buttonType="submit"
        disabled={isSubmitting}
        nextText={t('translation:buttons.submit', 'Submit')}
      />
    </form>
  );
}
